import { Action, createActionGroup, emptyProps, props } from '@ngrx/store';
import {
  BaseCalendarEventFragment,
  Charge,
  CreateCalendarEventInput,
  CreateChargeInput,
  CreateDiscountInput,
  Expense,
  FullDiscountFragment,
  ItineraryItem,
  JobDetailsV2Fragment,
  SelectedProduct,
  Tax
} from 'graphql.generated';
import { EventWithCharges, WorkorderEventAttachment } from 'src/app/job/job-workorders/job-workorders.interfaces';
import { ActionSource } from 'src/app/state/action-sources';

import {
  CalendarEventWithLockedAndInvoicedFlag,
  ChargesUpdate,
  CommentChange,
  UnsavedChangesToastInfo,
} from '../job-create/job-interfaces';

import { EditableCharge, EditableDiscount, EditableWorkorder } from './job-workorders.state';

export const WorkOrdersActions = createActionGroup({
  source: ActionSource.WORKORDERS,
  events: {
    'Toggle Event Button Clicked': props<{
      eventId: string;
    }>(),
    'Toggle All Events Button Clicked': emptyProps(),
    'Open Event Actions Button Clicked': props<{
      eventId: string;
    }>(),
    'Move to Top Menu Item Clicked': props<{ eventId: string }>(),
    'Move Up Menu Item Clicked': props<{ eventId: string }>(),
    'Move Down Menu Item Clicked': props<{ eventId: string }>(),
    'Move to Bottom Menu Item Clicked': props<{ eventId: string }>(),
    'Delete Menu Item Clicked': props<{ eventId: string }>(),
    'Schedule Event Menu Item Clicked': emptyProps(),
    'Save Menu Item Clicked': emptyProps(),
    'Promote Event Menu Item Clicked': emptyProps(),
    'Duplicate Menu Item Clicked': emptyProps(),
    'Duplicate Work Order Added': props<{
      workorder: EditableWorkorder;
    }>(),
    'Cancel Menu Item Clicked': props<{ eventId: string }>(),
    'View History Menu Item Clicked': emptyProps(),
    'Share Menu Item Clicked': emptyProps(),
    'Update Navigator Menu Item Clicked': props<{ eventId: string; initialStepId?: string }>(),
    'Update Navigator Confirmed': props<{
      eventId: string;
      currentStep: string;
      navigatorData: string;
    }>(),
    /**
     * Dispatched immediately after a successful updateEventNavigator mutation
     * from the dialog. Patches the job's event in the NgRx store so the updated
     * navigatorCurrentStep / navigatorData are persisted to localStorage via the
     * persistState effect — no subscription round-trip needed.
     */
    'Navigator Updated Locally': props<{
      jobId: string;
      eventId: string;
      navigatorCurrentStep: string | null;
      navigatorData: string | null;
    }>(),

    /**
     * Patches only `navigatorData`, leaving `navigatorCurrentStep` alone.
     *
     * The counterpart to `navigatorStepAdvancedLocally` (step only). Anything that records
     * something *about* a run rather than advancing it belongs here.
     *
     * `patchNavigatorData` used `navigatorUpdatedLocally` for this, which carries a step as
     * well — and it sourced that step from the `selectJob` snapshot taken when the action was
     * dispatched. So a patch completing after the crew had advanced stamped the pre-advance
     * step back over the optimistic one, reverting the crew a step with no server involvement
     * at all. See #46.
     */
    'Navigator Data Updated Locally': props<{
      jobId: string;
      eventId: string;
      navigatorData: string | null;
    }>(),

    /**
     * Moves the event's step in the store the moment the crew confirms, without waiting for the
     * 5-second undo window to elapse and the mutation to round-trip.
     *
     * Patches **only** `navigatorCurrentStep` — the mirror of `patchNavigatorData`, which writes
     * only `navigatorData`. The merged step timings and geolocation are assembled inside
     * `syncNavigatorStep`, so the component has no `navigatorData` worth writing here and must
     * not clobber what is there.
     *
     * Without this the store held the *old* step for five seconds while the UI showed the new
     * one, which made the workorders progress bar lag and let any `jobChanged` arriving inside
     * the window drag the crew back a step. See #31.
     */
    'Navigator Step Advanced Locally': props<{
      jobId: string;
      eventId: string;
      navigatorCurrentStep: string | null;
    }>(),

    'Rate Input Changed': props<{
      eventId: string;
      chargeId: string;
      amount: number;
    }>(),

    'Rate Input Key Down': props<{
      eventId: string;
      chargeId: string;
      event: KeyboardEvent;
    }>(),

    'Quantity Input Changed': props<{
      eventId: string;
      chargeId: string;
      quantity: number;
    }>(),

    'Charge Description Changed': props<{
      eventId: string;
      chargeId: string;
      description: string;
    }>(),

    'Notes Changed': props<{
      eventId: string;
      eventNotes: string;
    }>(),

    'Event Attachments Changed': props<{
      eventId: string;
      eventAttachments: WorkorderEventAttachment[];
    }>(),

    // Small event-level actions: the append/filter happens in the reducer
    // (atomic per event), not in a component that first awaits the current
    // list and dispatches the WHOLE array — two overlapping uploads or an
    // upload + remove reading stale lists could drop the other side's file
    // (CodeRabbit; keeps the payload as small as possible).
    'Event Attachment Uploaded': props<{
      eventId: string;
      attachment: WorkorderEventAttachment;
    }>(),

    'Remove Attachment Button Clicked': props<{
      eventId: string;
      artifactId: string;
    }>(),

    'Event Upload Started': props<{
      eventId: string;
    }>(),

    'Event Upload Finished': props<{
      eventId: string;
    }>(),

    'Open Charge Actions Button Clicked': props<{
      chargeId: string;
    }>(),
    'Remove Charge Menu Item Clicked': props<{ chargeId: string }>(),
    'Remove Charge Button Clicked': props<{ chargeId: string }>(),
    'Charge Dropped': props<{
      chargeId: string;
      newIndex: number;
      sourceEventId: string;
      targetEventId: string;
    }>(),

    'Open Add Charges Dialog Button Clicked': props<{ eventId: string }>(),

    'Product Search Input Changed': props<{ query: string }>(),

    'Product Quantity Input Changed': props<{ productId: string, quantity: number }>(),
    'Product Quantity Switch Clicked': props<{ productId: string }>(),

    'Cancel Add Charges Button Clicked': emptyProps(),
    'Exit Add Charges Dialog Button Clicked': emptyProps(),
    'Add Charges Button Clicked': emptyProps(),
    'Charges Added': props<{
      eventIds: string[];
      charges: EditableCharge[];
    }>(),

    'Add Custom Charge Button Clicked': props<{ eventId: string }>(),
    'Edit Charge Button Clicked': props<{
      eventId: string;
      chargeId: string;
    }>(),

    'Event Type Menu Item Selected': props<{ eventType: string, eventId: string }>(),
    'Fix Locations Link Clicked': props<{ eventType: string }>(),

    'Show Confidential Info Toggle Button Clicked': props<{
      eventId: string;
    }>(),

    'Add Discounts Button Clicked': props<{ eventId: string; discountId?: string; amount?: number }>(),
    'Open Discount Actions Button Clicked': props<{ appliedId: string }>(),
    'Remove Discount Menu Item Clicked': props<{ eventId: string; appliedId: string }>(),
    'Event Details Clicked': props<{ eventId: string }>(),
    'Book Event Button Clicked': props<{ eventId: string }>(),
    'Start Event Button Clicked': props<{ eventId: string }>(),
    'Work Orders Reloaded': props<{ job: JobDetailsV2Fragment }>(),

    //

    'Fill Out Events With Charges': props<{
      calculatedEvents: EventWithCharges[];
    }>(),

    'Add Charges Clicked': props<{
      eventId: string;
    }>(),
    'Add Discounts Clicked': props<{
      eventId: string;
    }>(),
    'Add Charges Closed': emptyProps(),
    'Add Discounts Closed': emptyProps(),

    'List Taxes Success': props<{ taxes: Tax[] }>(),
    'Expenses Loaded': props<{ expenses: Expense[] }>(),

    'Available Discounts Loading': emptyProps(),
    'Load Available Discounts Success': props<{
      discounts: FullDiscountFragment[];
    }>(),
    'Available Discounts Loaded Error': props<{ error: Error }>(),
    'Available Discounts Cleared': emptyProps(),

    'Product For Adding Selected': props<{
      productId: string;
      priceId: string;
      quantity: number;
    }>(),
    'Add Charges Saved': emptyProps(),

    'Existing Charges Updated': props<{
      chargeId?: string;
      quantity?: number;
      amount?: number;
      eventId?: string;
      removed?: boolean;
      orderUpdatedInput?: {
        targetEvent: Partial<CalendarEventWithLockedAndInvoicedFlag>;
        insertedCharge: Charge;
        insertedChargeIndex: number;
        isInsertedChargeNew?: boolean;
        donorEvent?: Partial<CalendarEventWithLockedAndInvoicedFlag>;
      }
    }>(),
    'Changes Saved Success': props<{
        updatedJob: JobDetailsV2Fragment,
        originalAction: Action,
        mutationId?: string,
    }>(),
    'Changes Saved Error': props<{
        error: Error,
        mutationId?: string,
    }>(),
    'Save Button Clicked': emptyProps(),
    'Cancel button Clicked': props<{ eventId?: string }>(),
    'changes Saved Initiated': props<{
       mutationId?: string;
    }>(),
    'Changes Saved No Changes': props<{
       originalAction: Action,
       //when changes recorded, should not be saved but should be removed from state and ls
       //e.g. charge was added and removed before saving and it was the only one change
       clearRecorderChanges?: boolean,
    }>(),

    'Add Discount': props<{
      discounts: Array<{
        discountId: string;
        eventId: string;
        customAmount?: number;
      }>;
    }>(),

    'Remove Discount': props<{
      appliedId: string;
    }>(),

    'Create Single Use Discount': props<{
      createDiscountInput: CreateDiscountInput;
      eventsIds: string[];
    }>(),

    'Discount Added': props<{
      discount: EditableDiscount;
      eventIds: string[]
    }>(),

    'Create Custom Charge Clicked': emptyProps(),
    'Close Custom Charge Dialog Clicked': emptyProps(),

    'Create Custom Charge': props<{
      createChargeInput: CreateChargeInput;
      userId: string; //job customer id
    }>(),

    'Apply Unsaved Changes From LS': props<{
      unsavedChanges: ChargesUpdate[];
      unsavedCommentChanges: CommentChange[];
    }>(),
    'Generate Unsaved Changes Toasts': props<{
      unsavedChangesToastsInfo: UnsavedChangesToastInfo[];
    }>(),
    'Update Charges Order': props<{
      targetEvent: Partial<CalendarEventWithLockedAndInvoicedFlag>;
      insertedCharge: Charge;
      insertedChargeIndex: number;
      isInsertedChargeNew?: boolean;
      donorEvent?: Partial<CalendarEventWithLockedAndInvoicedFlag>;
    }>(),

    'Create Event': props<{ eventInput: CreateCalendarEventInput }>(),

    'Duplicate Event': props<{
      eventInput: Partial<CalendarEventWithLockedAndInvoicedFlag>;
    }>(),

    'Cancel Event': props<{ eventId: string }>(),

    'Delete Event': props<{ eventId: string }>(),

    'Reorder Events': props<{
      eventsWithNewOrder: { eventId: string; newOrder: number }[];
    }>(),

    'Work Order Unsaved Changes Dumped to LS': emptyProps(),

    'Add Estimate Button Clicked': emptyProps(),

    'Confirm Cancel Event Button Clicked': props<{ eventId: string; }>(),

    'Generate Charges Button Clicked': props<{ eventId: string }>(),
    'Regenerate Charges With AI': props<{ eventId: string }>(),
    'Open Charges Generated With AI Summary': props<{ eventId: string, }>(),
    'Generate Charges With AI Success': props<{
       eventId: string,
       selectedProducts: SelectedProduct[],
       agentInstructions: string,
       itinerary: ItineraryItem[];
       crewSize: number;
       charges: EditableCharge[];
    }>(),
    'Generate Charges With AI Error': props<{ error: Error }>(),

    'Promote Event Button Clicked': props<{ event: BaseCalendarEventFragment }>(),
    'Promote Event Success': props<{ eventId: string, status: string }>(),
    'Promote Event Error': props<{ error: Error }>(),
    'Event Selected From Suggestions': props<{ ids: string[] }>(),

    'Set Area Button Clicked': emptyProps(),

    'Add Calculator Charge Error': props<{ error: Error }>(),
    },
  },
);
